import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  BuildingOfficeIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { Button, Input, Select } from '../components/ui';
import { useAuthStore } from '../stores';
import { signupSchema, type SignupFormData } from '../utils/validation';

const industryOptions = [
  { value: 'telehealth', label: 'Telehealth' },
  { value: 'pharmacy', label: 'Pharmacy' },
  { value: 'clinic', label: 'Medical Clinic' },
  { value: 'wellness', label: 'Wellness Center' },
  { value: 'other', label: 'Other' },
];

const stateOptions = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

export const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { signup, isLoading, error, clearError } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState<'business' | 'owner'>('business');

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      business: {
        name: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        phone: '',
        industry: '',
      },
      owner: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        phone: '',
      },
    },
  });

  const handleNextStep = async () => {
    const isValid = await trigger(['business.name', 'business.address', 'business.city', 'business.state', 'business.zipCode', 'business.phone', 'business.industry']);
    if (isValid) {
      setStep('owner');
    }
  };

  const onSubmit = async (data: SignupFormData) => {
    try {
      clearError();
      await signup(data);
      // Set flag to show onboarding stepper on first login
      localStorage.setItem('show_onboarding', 'true');
      toast.success('Account created successfully! Welcome to Capture Health.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(error || 'Registration failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 py-12 sm:px-6 lg:px-20 xl:px-24 overflow-y-auto">
        <div className="mx-auto w-full max-w-lg">
          {/* Logo */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <span className="text-white font-bold text-lg">CH</span>
              </div>
              <span className="font-display text-xl font-semibold text-secondary-900 dark:text-secondary-50">
                Capture Health
              </span>
            </div>
            <h2 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">Create your account</h2>
            <p className="mt-2 text-secondary-600 dark:text-secondary-400">
              Register your business to get started
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center mb-8">
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step === 'business' ? 'bg-primary-500 text-white' : 'bg-success-500 text-white'}`}>
                {step === 'owner' ? '✓' : '1'}
              </div>
              <span className={`ml-2 text-sm font-medium ${step === 'business' ? 'text-primary-500 dark:text-primary-400' : 'text-success-500 dark:text-success-400'}`}>
                Business Info
              </span>
            </div>
            <div className="flex-1 h-0.5 mx-4 bg-secondary-200 dark:bg-secondary-700">
              <div className={`h-full transition-all duration-300 ${step === 'owner' ? 'bg-primary-500 w-full' : 'w-0'}`} />
            </div>
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step === 'owner' ? 'bg-primary-500 text-white' : 'bg-secondary-200 dark:bg-secondary-700 text-secondary-500 dark:text-secondary-400'}`}>
                2
              </div>
              <span className={`ml-2 text-sm font-medium ${step === 'owner' ? 'text-primary-500 dark:text-primary-400' : 'text-secondary-500 dark:text-secondary-400'}`}>
                Owner Info
              </span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {step === 'business' ? (
              <>
                <Input
                  label="Business Name"
                  placeholder="Your Company LLC"
                  leftIcon={<BuildingOfficeIcon className="h-5 w-5" />}
                  error={errors.business?.name?.message}
                  {...register('business.name')}
                />

                <Input
                  label="Street Address"
                  placeholder="123 Main Street"
                  leftIcon={<MapPinIcon className="h-5 w-5" />}
                  error={errors.business?.address?.message}
                  {...register('business.address')}
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="City"
                    placeholder="New York"
                    error={errors.business?.city?.message}
                    {...register('business.city')}
                  />
                  <Select
                    label="State"
                    placeholder="Select state"
                    options={stateOptions}
                    error={errors.business?.state?.message}
                    {...register('business.state')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="ZIP Code"
                    placeholder="10001"
                    error={errors.business?.zipCode?.message}
                    {...register('business.zipCode')}
                  />
                  <Input
                    label="Phone"
                    placeholder="(555) 123-4567"
                    leftIcon={<PhoneIcon className="h-5 w-5" />}
                    error={errors.business?.phone?.message}
                    {...register('business.phone')}
                  />
                </div>

                <Select
                  label="Industry"
                  placeholder="Select industry"
                  options={industryOptions}
                  error={errors.business?.industry?.message}
                  {...register('business.industry')}
                />

                <Button
                  type="button"
                  className="w-full"
                  size="lg"
                  onClick={handleNextStep}
                >
                  Continue
                </Button>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    placeholder="John"
                    leftIcon={<UserIcon className="h-5 w-5" />}
                    error={errors.owner?.firstName?.message}
                    {...register('owner.firstName')}
                  />
                  <Input
                    label="Last Name"
                    placeholder="Doe"
                    error={errors.owner?.lastName?.message}
                    {...register('owner.lastName')}
                  />
                </div>

                <Input
                  label="Email Address"
                  type="email"
                  placeholder="john@company.com"
                  leftIcon={<EnvelopeIcon className="h-5 w-5" />}
                  error={errors.owner?.email?.message}
                  {...register('owner.email')}
                />

                <Input
                  label="Phone"
                  placeholder="(555) 123-4567"
                  leftIcon={<PhoneIcon className="h-5 w-5" />}
                  error={errors.owner?.phone?.message}
                  {...register('owner.phone')}
                />

                <div className="relative">
                  <Input
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    leftIcon={<LockClosedIcon className="h-5 w-5" />}
                    rightIcon={
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="focus:outline-none"
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    }
                    error={errors.owner?.password?.message}
                    helperText="At least 8 characters with uppercase, lowercase, and numbers"
                    {...register('owner.password')}
                  />
                </div>

                <div className="flex space-x-3">
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex-1"
                    size="lg"
                    onClick={() => setStep('business')}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    size="lg"
                    isLoading={isLoading}
                  >
                    Create Account
                  </Button>
                </div>
              </>
            )}
          </form>

          <p className="mt-8 text-center text-sm text-secondary-600 dark:text-secondary-400">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary-500 hover:text-primary-600">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:block relative flex-1 gradient-mesh bg-secondary-50 dark:bg-secondary-800">
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="max-w-lg text-center">
            <div className="w-24 h-24 mx-auto mb-8 rounded-2xl bg-white dark:bg-secondary-700 shadow-elevated flex items-center justify-center">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <span className="text-white font-bold text-2xl">CH</span>
              </div>
            </div>
            <h3 className="text-2xl font-display font-semibold text-secondary-900 dark:text-secondary-50 mb-4">
              Join Capture Health Today
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400">
              Get access to powerful tools for managing your telehealth business,
              from order processing to patient management.
            </p>
            <div className="mt-8 space-y-4 text-left max-w-sm mx-auto">
              <div className="flex items-center space-x-3 p-3 bg-white/80 dark:bg-secondary-700/80 backdrop-blur-sm rounded-lg shadow-soft">
                <div className="w-8 h-8 rounded-full bg-success-100 dark:bg-success-900/30 flex items-center justify-center">
                  <span className="text-success-600 dark:text-success-400">✓</span>
                </div>
                <span className="text-sm text-secondary-700 dark:text-secondary-300">Real-time order tracking</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-white/80 dark:bg-secondary-700/80 backdrop-blur-sm rounded-lg shadow-soft">
                <div className="w-8 h-8 rounded-full bg-success-100 dark:bg-success-900/30 flex items-center justify-center">
                  <span className="text-success-600 dark:text-success-400">✓</span>
                </div>
                <span className="text-sm text-secondary-700 dark:text-secondary-300">Patient management system</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-white/80 dark:bg-secondary-700/80 backdrop-blur-sm rounded-lg shadow-soft">
                <div className="w-8 h-8 rounded-full bg-success-100 dark:bg-success-900/30 flex items-center justify-center">
                  <span className="text-success-600 dark:text-success-400">✓</span>
                </div>
                <span className="text-sm text-secondary-700 dark:text-secondary-300">Customizable email templates</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;

